import { useEffect } from "react";
import type { FallbackRender } from "@sentry/react";

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    (msg.includes("unexpected token") && msg.includes("<"))
  );
}

const ErrorFallback: FallbackRender = ({ error, resetError }) => {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;

    const hasRetried = sessionStorage.getItem("chunk-error-reload");
    if (!hasRetried) {
      sessionStorage.setItem("chunk-error-reload", "1");
      window.location.reload();
    }
  }, [error]);

  if (isChunkLoadError(error)) {
    const hasRetried = sessionStorage.getItem("chunk-error-reload");
    if (hasRetried) {
      sessionStorage.removeItem("chunk-error-reload");
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-global-bg px-4">
        <div className="flex flex-col items-center gap-3 text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-global-accent/10 flex items-center justify-center">
            <svg
              className="w-7 h-7 text-global-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h1 className="text-size-lg font-semibold text-global-title">
            Nueva version disponible
          </h1>
          <p className="text-size-sm text-global-muted">
            La aplicacion se ha actualizado. Por favor, recarga la pagina.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-compact btn-global-primary font-semibold"
        >
          Recargar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-global-bg px-4">
      <div className="flex flex-col items-center gap-3 text-center max-w-md">
        <div className="w-14 h-14 rounded-full bg-global-error/10 flex items-center justify-center">
          <svg
            className="w-7 h-7 text-global-error"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h1 className="text-size-lg font-semibold text-global-title">
          Algo salio mal
        </h1>
        <p className="text-size-sm text-global-muted">
          Ocurrio un error inesperado. El equipo ya fue notificado.
        </p>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={resetError}
          className="btn-compact btn-global-primary font-semibold"
        >
          Reintentar
        </button>
        <button
          type="button"
          onClick={() => window.location.assign("/")}
          className="btn-compact btn-global-secondary font-semibold"
        >
          Ir al inicio
        </button>
      </div>
    </div>
  );
};

export default ErrorFallback;
